import React from 'react';

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(cents || 0) / 100);
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
}

function AcceptedQuoteSummary({acceptance,acceptanceLoading,acceptanceError}) {
  if (acceptanceLoading) {
    return (
      <section
        aria-live="polite"
        style={{
          marginBottom: '16px',
          padding: '24px',
          background: '#edf4ef',
          border: '2px solid #7ca18a',
          boxShadow: '0 4px 14px rgba(65,99,77,.08)'
        }}
      >
        <span
          className="eyebrow"
          style={{color: 'var(--green)',marginBottom: '8px'}}
        >
          Acceptance
        </span>
        <h2
          style={{
            color: 'var(--green)',
            fontFamily: 'Manrope,sans-serif',
            fontSize: '20px',
            letterSpacing: '-.025em',
            textTransform: 'uppercase'
          }}
        >
          Loading client acceptance…
        </h2>
      </section>
    );
  }

  if (acceptanceError) {
    return (
      <section
        aria-live="assertive"
        style={{
          marginBottom: '16px',
          padding: '24px',
          background: '#fff4ef',
          border: '2px solid var(--accent)',
          boxShadow: '0 4px 14px rgba(154,79,54,.08)'
        }}
      >
        <span
          className="eyebrow"
          style={{color: 'var(--accent-dark)',marginBottom: '8px'}}
        >
          Acceptance
        </span>
        <h2
          style={{
            color: 'var(--accent-dark)',
            fontFamily: 'Manrope,sans-serif',
            fontSize: '20px',
            letterSpacing: '-.025em',
            textTransform: 'uppercase'
          }}
        >
          Client acceptance could not be loaded
        </h2>
        <p style={{marginTop: '8px',color: 'var(--accent-dark)',fontSize: '12px'}}>
          {acceptanceError}
        </p>
      </section>
    );
  }

  if (!acceptance) {
    return null;
  }

  const selectedExtras = Array.isArray(acceptance.selected_extras)
    ? acceptance.selected_extras
    : [];
  const hasSelectedExtras = selectedExtras.length > 0;

  return (
    <section
      aria-labelledby="accepted-quote-summary-title"
      style={{
        marginBottom: '16px',
        padding: '24px',
        background: '#edf4ef',
        border: '2px solid #7ca18a',
        boxShadow: '0 4px 14px rgba(65,99,77,.08)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '20px',
          marginBottom: '18px'
        }}
      >
        <div>
          <span
            className="eyebrow"
            style={{
              color: 'var(--green)',
              marginBottom: '7px',
              fontWeight: 800
            }}
          >
            Acceptance
          </span>
          <h2
            id="accepted-quote-summary-title"
            style={{
              color: 'var(--green)',
              fontFamily: 'Manrope,sans-serif',
              fontSize: '20px',
              letterSpacing: '-.025em',
              textTransform: 'uppercase'
            }}
          >
            Client Accepted with Optional Extras
          </h2>
          <p style={{marginTop: '7px',color: 'var(--muted)',fontSize: '12px'}}>
            This is the client&apos;s accepted scope, saved at acceptance time.
          </p>
        </div>
        <span
          style={{
            color: 'var(--green)',
            fontSize: '11px',
            fontWeight: 800,
            whiteSpace: 'nowrap'
          }}
        >
          Read-only
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
          gap: '8px 24px',
          marginBottom: '20px',
          color: 'var(--muted)',
          fontSize: '12px'
        }}
      >
        <div>
          <strong style={{color: 'var(--ink)'}}>Accepted by:</strong>{' '}
          {acceptance.full_name || '—'}
        </div>
        <div>
          <strong style={{color: 'var(--ink)'}}>Company:</strong>{' '}
          {acceptance.company || '—'}
        </div>
        <div>
          <strong style={{color: 'var(--ink)'}}>Email:</strong>{' '}
          {acceptance.email || '—'}
        </div>
        <div>
          <strong style={{color: 'var(--ink)'}}>Accepted:</strong>{' '}
          {formatDate(acceptance.accepted_at)}
        </div>
      </div>

      <div
        style={{
          padding: '17px',
          background: 'rgba(255,255,255,.62)',
          border: '1px solid #b7cdbd'
        }}
      >
        <h3
          style={{
            marginBottom: '13px',
            color: 'var(--green)',
            fontSize: '12px',
            fontWeight: 800,
            letterSpacing: '.12em',
            textTransform: 'uppercase'
          }}
        >
          Client Selected Optional Extras
        </h3>

        {hasSelectedExtras ? (
          <div style={{display: 'grid',gap: '9px'}}>
            {selectedExtras.map((extra,index) => (
              <div
                key={extra.id || `${extra.name}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '20px',
                  paddingBottom: '9px',
                  borderBottom: '1px solid rgba(65,99,77,.18)',
                  color: 'var(--ink)',
                  fontSize: '13px'
                }}
              >
                <span>
                  <strong
                    aria-hidden="true"
                    style={{
                      marginRight: '8px',
                      color: 'var(--green)',
                      fontSize: '15px'
                    }}
                  >
                    ✓
                  </strong>
                  {extra.name}
                </span>
                <strong>{formatCurrency(extra.price_cents)}</strong>
              </div>
            ))}
          </div>
        ) : (
          <p
            style={{
              padding: '13px',
              background: '#f6faf7',
              color: 'var(--green)',
              fontSize: '13px',
              fontWeight: 700
            }}
          >
            No optional additional deliverables were selected by the client.
          </p>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gap: '9px',
          marginTop: '20px',
          paddingTop: '17px',
          borderTop: '1px solid #7ca18a',
          color: 'var(--muted)',
          fontSize: '12px'
        }}
      >
        <div style={{display: 'flex',justifyContent: 'space-between',gap: '20px'}}>
          <span>Base quotation</span>
          <strong style={{color: 'var(--ink)'}}>
            {formatCurrency(acceptance.base_total_cents)}
          </strong>
        </div>
        <div style={{display: 'flex',justifyContent: 'space-between',gap: '20px'}}>
          <span>Client-selected extras</span>
          <strong style={{color: 'var(--ink)'}}>
            {formatCurrency(acceptance.extras_total_cents)}
          </strong>
        </div>
        <div style={{display: 'flex',justifyContent: 'space-between',gap: '20px'}}>
          <span>GST</span>
          <strong style={{color: 'var(--ink)'}}>
            {formatCurrency(acceptance.gst_cents)}
          </strong>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '20px',
            marginTop: '4px',
            paddingTop: '13px',
            borderTop: '2px solid var(--green)',
            color: 'var(--green)',
            fontSize: '16px',
            fontWeight: 800
          }}
        >
          <span>Final accepted total</span>
          <strong>{formatCurrency(acceptance.total_inc_gst_cents)}</strong>
        </div>
      </div>
    </section>
  );
}

export default AcceptedQuoteSummary;